import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  FiChevronDown,
  FiChevronUp,
  FiLayers,
  FiPlus,
  FiSearch,
  FiTrash2,
} from 'react-icons/fi';
import {
  createProductVarietyGroup,
  deleteProductVarietyGroup,
  reorderProductVarietyGroups,
  updateProductVarietyGroup,
} from '../api';
import { normalizeVarietyGroups } from '../utils/varietyGroups';
import InlineLoading from './InlineLoading';

const NEW_GROUP_ID = 'new';

const toId = (value) => (value === null || value === undefined ? '' : String(value));

const normalizeSearchText = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLocaleLowerCase('es');

const haveSameIds = (left = [], right = []) => {
  const normalize = (values) => Array.from(new Set(values.map(Number))).sort((a, b) => a - b);
  return JSON.stringify(normalize(left)) === JSON.stringify(normalize(right));
};

const getRequestError = (error) => (
  error?.response?.data?.message
  || error?.message
  || 'No se pudo guardar la configuración del grupo.'
);

const getVarietyName = (variety) => (
  String(variety?.varietyName || variety?.name || '').trim()
  || `Variedad ${variety?.id}`
);

const VarietyGroupConfigurationModal = ({
  isOpen = false,
  onClose,
  branchId,
  product,
  canUpdateProduct = true,
  initialGroupId,
  onCreateVariety,
}) => {
  const sourceGroups = useMemo(
    () => normalizeVarietyGroups(product?.VarietyGroups),
    [product?.VarietyGroups]
  );
  const varieties = useMemo(
    () => (Array.isArray(product?.Varieties) ? product.Varieties : [])
      .filter((variety) => Number.isInteger(Number(variety?.id))),
    [product?.Varieties]
  );
  const [groups, setGroups] = useState(sourceGroups);
  const [activeEditorId, setActiveEditorId] = useState(
    sourceGroups[0] ? toId(sourceGroups[0].id) : NEW_GROUP_ID
  );
  const [draftName, setDraftName] = useState(sourceGroups[0]?.name || '');
  const [draftVarietyIds, setDraftVarietyIds] = useState(sourceGroups[0]?.varietyIds || []);
  const [search, setSearch] = useState('');
  const [varietyFilter, setVarietyFilter] = useState('all');
  const [savingAction, setSavingAction] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const wasOpenRef = useRef(false);
  const openProductIdRef = useRef(null);
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const nameInputRef = useRef(null);
  const feedbackRef = useRef(null);
  const requestCloseRef = useRef(null);

  const selectedGroup = groups.find((group) => toId(group.id) === activeEditorId) || null;
  const isCreating = activeEditorId === NEW_GROUP_ID || !selectedGroup;
  const selectedIdSet = useMemo(
    () => new Set(draftVarietyIds.map(Number)),
    [draftVarietyIds]
  );
  const isDirty = isCreating
    ? Boolean(draftName.trim() || draftVarietyIds.length)
    : draftName.trim() !== selectedGroup.name
      || !haveSameIds(draftVarietyIds, selectedGroup.varietyIds);
  const isSaving = Boolean(savingAction);

  const loadEditor = (group) => {
    setActiveEditorId(group ? toId(group.id) : NEW_GROUP_ID);
    setDraftName(group?.name || '');
    setDraftVarietyIds(group?.varietyIds || []);
    setSearch('');
    setVarietyFilter('all');
    setError('');
    setStatus('');
    setConfirmingDelete(false);
    setConfirmingClose(false);
  };

  useEffect(() => {
    const productChanged = toId(openProductIdRef.current) !== toId(product?.id);
    if (isOpen && (!wasOpenRef.current || productChanged)) {
      const nextGroups = normalizeVarietyGroups(product?.VarietyGroups);
      setGroups(nextGroups);
      const firstGroup = nextGroups.find((group) => toId(group.id) === toId(initialGroupId)) || nextGroups[0] || null;
      setActiveEditorId(firstGroup ? toId(firstGroup.id) : NEW_GROUP_ID);
      setDraftName(firstGroup?.name || '');
      setDraftVarietyIds(firstGroup?.varietyIds || []);
      setSearch('');
      setVarietyFilter('all');
      setSavingAction('');
      setError('');
      setStatus('');
      setConfirmingDelete(false);
      setConfirmingClose(false);
      openProductIdRef.current = product?.id ?? null;
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, product?.id, product?.VarietyGroups, initialGroupId]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const requestClose = () => {
    if (isSaving) return;
    if (canUpdateProduct && isDirty) {
      setConfirmingClose(true);
      setConfirmingDelete(false);
      setError('');
      setStatus('');
      return;
    }
    onClose?.();
  };

  requestCloseRef.current = requestClose;

  useEffect(() => {
    if (!isOpen) return undefined;
    const previouslyFocused = document.activeElement;
    const focusTarget = sourceGroups.length || nameInputRef.current?.disabled
      ? closeButtonRef.current
      : nameInputRef.current;
    focusTarget?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        requestCloseRef.current?.();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      )).filter((element) => element.getAttribute('aria-hidden') !== 'true');
      if (!focusable.length) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const focusIsOutside = !dialogRef.current.contains(document.activeElement);
      if (event.shiftKey && (document.activeElement === first || focusIsOutside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || focusIsOutside)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocused?.isConnected) previouslyFocused.focus();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!confirmingDelete && !confirmingClose && !error && !status) return;
    feedbackRef.current?.focus();
  }, [confirmingClose, confirmingDelete, error, status]);

  const groupNamesByVarietyId = useMemo(() => {
    const memberships = new Map();
    groups.forEach((group) => {
      (group.varietyIds || []).forEach((varietyId) => {
        const id = Number(varietyId);
        const names = memberships.get(id) || [];
        names.push({ id: toId(group.id), name: group.name });
        memberships.set(id, names);
      });
    });
    return memberships;
  }, [groups]);

  const effectiveMembershipCounts = useMemo(() => {
    const counts = new Map();
    groups.forEach((group) => {
      const ids = selectedGroup && toId(group.id) === toId(selectedGroup.id)
        ? draftVarietyIds
        : group.varietyIds;
      (ids || []).forEach((varietyId) => {
        const id = Number(varietyId);
        counts.set(id, (counts.get(id) || 0) + 1);
      });
    });
    if (isCreating) {
      draftVarietyIds.forEach((varietyId) => {
        const id = Number(varietyId);
        counts.set(id, (counts.get(id) || 0) + 1);
      });
    }
    return counts;
  }, [draftVarietyIds, groups, isCreating, selectedGroup]);

  const unassignedCount = varieties.filter(
    (variety) => !effectiveMembershipCounts.get(Number(variety.id))
  ).length;
  const multiGroupCount = varieties.filter(
    (variety) => (effectiveMembershipCounts.get(Number(variety.id)) || 0) > 1
  ).length;

  const filteredVarieties = useMemo(() => {
    const query = normalizeSearchText(search.trim());
    return varieties.filter((variety) => {
      const id = Number(variety.id);
      if (query && !normalizeSearchText(getVarietyName(variety)).includes(query)) return false;
      if (varietyFilter === 'selected') return selectedIdSet.has(id);
      if (varietyFilter === 'unassigned') return !effectiveMembershipCounts.get(id);
      if (varietyFilter === 'other-groups') {
        return (groupNamesByVarietyId.get(id) || []).some(
          (group) => group.id !== toId(selectedGroup?.id)
        );
      }
      return true;
    });
  }, [
    effectiveMembershipCounts,
    groupNamesByVarietyId,
    search,
    selectedGroup?.id,
    selectedIdSet,
    varieties,
    varietyFilter,
  ]);

  const switchEditor = (group) => {
    if (isSaving) return;
    const nextId = group ? toId(group.id) : NEW_GROUP_ID;
    if (nextId === activeEditorId) return;
    if (isDirty) {
      setError('Guarda o descarta los cambios antes de configurar otro grupo.');
      return;
    }
    loadEditor(group);
    if (!group) window.setTimeout(() => nameInputRef.current?.focus(), 0);
  };

  const discardChanges = () => {
    loadEditor(selectedGroup);
  };

  const toggleVariety = (varietyId) => {
    if (!canUpdateProduct || isSaving) return;
    const id = Number(varietyId);
    setDraftVarietyIds((current) => {
      const next = new Set(current.map(Number));
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return varieties
        .map((variety) => Number(variety.id))
        .filter((candidateId) => next.has(candidateId));
    });
    setError('');
    setStatus('');
    setConfirmingClose(false);
  };

  const selectVisibleVarieties = () => {
    const visibleIds = new Set(filteredVarieties.map((variety) => Number(variety.id)));
    setDraftVarietyIds(
      varieties
        .map((variety) => Number(variety.id))
        .filter((id) => selectedIdSet.has(id) || visibleIds.has(id))
    );
    setError('');
    setStatus('');
    setConfirmingClose(false);
  };

  const clearSelectedVarieties = () => {
    setDraftVarietyIds([]);
    setError('');
    setStatus('');
    setConfirmingClose(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canUpdateProduct || isSaving || !branchId || !product?.id) return;
    const name = draftName.trim();
    if (!name) {
      setError('El nombre del grupo es obligatorio.');
      return;
    }
    const duplicateName = groups.some((group) => (
      toId(group.id) !== toId(selectedGroup?.id)
      && normalizeSearchText(group.name) === normalizeSearchText(name)
    ));
    if (duplicateName) {
      setError('Ya existe un grupo con ese nombre para este producto.');
      return;
    }

    setSavingAction(isCreating ? 'create' : 'save');
    setError('');
    setStatus('');
    setConfirmingClose(false);
    try {
      const membershipChanged = isCreating
        || !haveSameIds(draftVarietyIds, selectedGroup?.varietyIds);
      const nameChanged = isCreating || name !== selectedGroup?.name;
      const response = isCreating
        ? await createProductVarietyGroup(branchId, product.id, {
          name,
          varietyIds: draftVarietyIds,
        })
        : await updateProductVarietyGroup(branchId, product.id, selectedGroup.id, {
          ...(nameChanged ? { name } : {}),
          ...(membershipChanged ? { varietyIds: draftVarietyIds } : {}),
        });
      const nextGroups = normalizeVarietyGroups(response?.groups || groups);
      const savedGroup = response?.group
        || nextGroups.find((group) => toId(group.id) === toId(selectedGroup?.id))
        || nextGroups[nextGroups.length - 1]
        || null;
      setGroups(nextGroups);
      setActiveEditorId(savedGroup ? toId(savedGroup.id) : NEW_GROUP_ID);
      setDraftName(savedGroup?.name || name);
      setDraftVarietyIds(savedGroup?.varietyIds || draftVarietyIds);
      setSearch('');
      setVarietyFilter('all');
      setConfirmingDelete(false);
      setStatus(isCreating ? 'Grupo creado correctamente.' : 'Cambios guardados correctamente.');
      return savedGroup;
    } catch (requestError) {
      setError(getRequestError(requestError));
    } finally {
      setSavingAction('');
    }
  };

  const handleMove = async (groupId, direction) => {
    if (!canUpdateProduct || isSaving || !branchId || !product?.id) return;
    if (isDirty) {
      setError('Guarda o descarta los cambios antes de cambiar el orden.');
      return;
    }
    const currentIndex = groups.findIndex((group) => toId(group.id) === toId(groupId));
    const nextIndex = currentIndex + direction;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= groups.length) return;
    const reordered = groups.slice();
    const [movedGroup] = reordered.splice(currentIndex, 1);
    reordered.splice(nextIndex, 0, movedGroup);
    const optimisticGroups = reordered.map((group, index) => ({ ...group, sortOrder: index }));

    setSavingAction(`order-${toId(groupId)}`);
    setError('');
    setStatus('');
    setGroups(optimisticGroups);
    try {
      const response = await reorderProductVarietyGroups(
        branchId,
        product.id,
        optimisticGroups.map((group) => group.id)
      );
      setGroups(normalizeVarietyGroups(response?.groups || optimisticGroups));
      setStatus('Orden de los grupos actualizado.');
    } catch (requestError) {
      setGroups(groups);
      setError(getRequestError(requestError));
    } finally {
      setSavingAction('');
    }
  };

  const handleDelete = async () => {
    if (!selectedGroup || !canUpdateProduct || isSaving || !branchId || !product?.id) return;
    const removedIndex = groups.findIndex((group) => toId(group.id) === toId(selectedGroup.id));
    setSavingAction('delete');
    setError('');
    setStatus('');
    try {
      const response = await deleteProductVarietyGroup(
        branchId,
        product.id,
        selectedGroup.id
      );
      const nextGroups = normalizeVarietyGroups(
        response?.groups || groups.filter((group) => toId(group.id) !== toId(selectedGroup.id))
      );
      setGroups(nextGroups);
      const nextGroup = nextGroups[Math.min(Math.max(removedIndex, 0), nextGroups.length - 1)] || null;
      setActiveEditorId(nextGroup ? toId(nextGroup.id) : NEW_GROUP_ID);
      setDraftName(nextGroup?.name || '');
      setDraftVarietyIds(nextGroup?.varietyIds || []);
      setSearch('');
      setVarietyFilter('all');
      setConfirmingDelete(false);
      setConfirmingClose(false);
      setStatus('Grupo eliminado correctamente.');
    } catch (requestError) {
      setError(getRequestError(requestError));
    } finally {
      setSavingAction('');
    }
  };

  if (!isOpen) return null;

  const modal = (
    <div
      className="fixed inset-0 z-[1600] flex items-start justify-center overflow-y-auto bg-black/55 px-3 py-4 sm:px-4 sm:py-8"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="variety-group-configuration-title"
        aria-describedby="variety-group-configuration-description"
        aria-busy={isSaving}
        className="flex max-h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl sm:max-h-[90vh]"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="flex flex-none items-start justify-between gap-4 border-b border-gray-100 bg-gray-50 px-4 py-4 sm:px-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Catálogo de productos</p>
            <h2 id="variety-group-configuration-title" className="mt-1 text-lg font-semibold text-gray-900 sm:text-xl">
              Configurar grupos de variedades
            </h2>
            <p id="variety-group-configuration-description" className="mt-1 text-xs text-gray-500 sm:text-sm">
              Organiza las pestañas de {product?.productName || 'este producto'} y elige qué variedades aparecen en cada una.
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={requestClose}
            disabled={isSaving}
            aria-label="Cerrar configuración de grupos"
            className="flex h-10 w-10 flex-none items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span aria-hidden="true">✕</span>
          </button>
        </header>

        <div className="grid min-h-0 flex-1 overflow-y-auto lg:grid-cols-[300px,minmax(0,1fr)] lg:overflow-hidden">
          <aside className="border-b border-gray-200 bg-gray-50/70 p-4 lg:overflow-y-auto lg:border-b-0 lg:border-r">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Grupos</h3>
                <p className="mt-0.5 text-xs text-gray-500">
                  {groups.length} {groups.length === 1 ? 'grupo configurado' : 'grupos configurados'}
                </p>
              </div>
              {canUpdateProduct && (
                <button
                  type="button"
                  onClick={() => switchEditor(null)}
                  disabled={isSaving}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FiPlus aria-hidden="true" />
                  Nuevo
                </button>
              )}
            </div>

            <p className="mt-3 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700">
              El orden de esta lista define el orden de las pestañas que verá el usuario.
            </p>

            <div className="mt-3 max-h-72 space-y-2 overflow-y-auto pr-1 lg:max-h-none lg:overflow-visible lg:pr-0" aria-label="Lista ordenada de grupos">
              {groups.map((group, index) => {
                const isActive = toId(group.id) === activeEditorId;
                return (
                  <div
                    key={group.id}
                    className={`flex items-stretch overflow-hidden rounded-xl border bg-white transition ${
                      isActive ? 'border-blue-400 ring-2 ring-blue-100' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => switchEditor(group)}
                      aria-current={isActive ? 'true' : undefined}
                      className="min-w-0 flex-1 px-3 py-2.5 text-left"
                    >
                      <span className="flex items-start gap-2">
                        <span className={`mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-md text-xs font-bold ${
                          isActive ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {index + 1}
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-semibold text-gray-900">{group.name}</span>
                          <span className="mt-0.5 block text-xs text-gray-500">
                            {(group.varietyIds || []).length} {(group.varietyIds || []).length === 1 ? 'variedad' : 'variedades'}
                          </span>
                        </span>
                      </span>
                    </button>
                    {canUpdateProduct && (
                      <div className="flex w-11 flex-col border-l border-gray-100">
                        <button
                          type="button"
                          onClick={() => handleMove(group.id, -1)}
                          disabled={isSaving || isDirty || index === 0}
                          aria-label={`Mover ${group.name} hacia arriba`}
                          title="Mover hacia arriba"
                          className="flex flex-1 items-center justify-center text-gray-500 hover:bg-gray-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-30"
                        >
                          <FiChevronUp aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMove(group.id, 1)}
                          disabled={isSaving || isDirty || index === groups.length - 1}
                          aria-label={`Mover ${group.name} hacia abajo`}
                          title="Mover hacia abajo"
                          className="flex flex-1 items-center justify-center border-t border-gray-100 text-gray-500 hover:bg-gray-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-30"
                        >
                          <FiChevronDown aria-hidden="true" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              {groups.length === 0 && (
                <div className="rounded-xl border border-dashed border-gray-300 bg-white px-4 py-6 text-center">
                  <FiLayers className="mx-auto text-gray-400" size={24} aria-hidden="true" />
                  <p className="mt-2 text-sm font-semibold text-gray-700">Aún no hay grupos</p>
                  <p className="mt-1 text-xs text-gray-500">Crea el primero y agrega sus variedades en un solo paso.</p>
                </div>
              )}
            </div>
          </aside>

          <form className="flex min-h-0 flex-col lg:overflow-hidden" onSubmit={handleSubmit}>
            <div className="flex-1 space-y-5 p-4 sm:p-6 lg:overflow-y-auto">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {isCreating ? 'Nuevo grupo' : 'Configuración del grupo'}
                  </p>
                  <h3 className="mt-1 text-lg font-semibold text-gray-900">
                    {isCreating ? 'Crea el grupo completo' : selectedGroup?.name}
                  </h3>
                  <p className="mt-1 text-xs text-gray-500">
                    Una variedad puede pertenecer a más de un grupo.
                  </p>
                </div>
                {!isCreating && canUpdateProduct && (
                  <button
                    type="button"
                    onClick={() => {
                      setConfirmingDelete(true);
                      setConfirmingClose(false);
                      setError('');
                      setStatus('');
                    }}
                    disabled={isSaving}
                    className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <FiTrash2 aria-hidden="true" />
                    Eliminar grupo
                  </button>
                )}
              </div>

              {confirmingDelete && selectedGroup && (
                <div
                  ref={feedbackRef}
                  role="alert"
                  tabIndex={-1}
                  className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 outline-none focus:ring-2 focus:ring-red-300"
                >
                  <p className="font-semibold">¿Eliminar “{selectedGroup.name}”?</p>
                  <p className="mt-1 text-xs text-red-700">
                    Las variedades no se eliminan; solo dejarán de pertenecer a este grupo.
                  </p>
                  <div className="mt-3 flex flex-wrap justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setConfirmingDelete(false)}
                      disabled={isSaving}
                      className="rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                    >
                      Conservar grupo
                    </button>
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={isSaving}
                      className="rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      Sí, eliminar
                    </button>
                  </div>
                </div>
              )}

              {confirmingClose && (
                <div
                  ref={feedbackRef}
                  role="alert"
                  tabIndex={-1}
                  className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 outline-none focus:ring-2 focus:ring-amber-300"
                >
                  <p className="font-semibold">Hay cambios sin guardar.</p>
                  <p className="mt-1 text-xs text-amber-800">Si cierras ahora, perderás los cambios de este grupo.</p>
                  <div className="mt-3 flex flex-wrap justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setConfirmingClose(false)}
                      className="rounded-lg border border-amber-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                    >
                      Seguir editando
                    </button>
                    <button
                      type="button"
                      onClick={() => onClose?.()}
                      className="rounded-lg bg-amber-600 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-700"
                    >
                      Descartar y cerrar
                    </button>
                  </div>
                </div>
              )}

              {error && (
                <p
                  ref={feedbackRef}
                  role="alert"
                  tabIndex={-1}
                  className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 outline-none focus:ring-2 focus:ring-red-300"
                >
                  {error}
                </p>
              )}
              {status && !error && (
                <p
                  ref={feedbackRef}
                  role="status"
                  tabIndex={-1}
                  className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700 outline-none focus:ring-2 focus:ring-green-300"
                >
                  {status}
                </p>
              )}

              <div>
                <label htmlFor="variety-group-name" className="text-xs font-semibold uppercase text-gray-600">
                  Nombre del grupo
                </label>
                <input
                  ref={nameInputRef}
                  id="variety-group-name"
                  type="text"
                  value={draftName}
                  onChange={(event) => {
                    setDraftName(event.target.value);
                    setError('');
                    setStatus('');
                    setConfirmingClose(false);
                  }}
                  disabled={!canUpdateProduct || isSaving}
                  placeholder="Ej. Color, Tamaño o Maduración"
                  className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100"
                />
              </div>

              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3">
                  <span className="block text-lg font-bold text-gray-900">{draftVarietyIds.length}</span>
                  <span className="text-[11px] font-medium text-gray-500 sm:text-xs">Seleccionadas</span>
                </div>
                <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3">
                  <span className="block text-lg font-bold text-gray-900">{unassignedCount}</span>
                  <span className="text-[11px] font-medium text-gray-500 sm:text-xs">Sin grupo</span>
                </div>
                <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3">
                  <span className="block text-lg font-bold text-gray-900">{multiGroupCount}</span>
                  <span className="text-[11px] font-medium text-gray-500 sm:text-xs">En varios grupos</span>
                </div>
              </div>

              <fieldset disabled={isSaving}>
                <legend className="text-xs font-semibold uppercase text-gray-600">Variedades del grupo</legend>
                {canUpdateProduct && onCreateVariety && (
                  <button
                    type="button"
                    disabled={isSaving}
                    onClick={async (event) => {
                      const group = isCreating || isDirty ? await handleSubmit(event) : selectedGroup;
                      if (group) onCreateVariety(group.id);
                    }}
                    className="mt-2 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    <FiPlus aria-hidden="true" />
                    {isCreating || isDirty ? 'Guardar grupo y crear variedad' : 'Crear variedad en este grupo'}
                  </button>
                )}
                <div className="mt-2 grid gap-2 sm:grid-cols-[minmax(0,1fr),190px]">
                  <label className="relative block">
                    <span className="sr-only">Buscar variedades</span>
                    <FiSearch
                      aria-hidden="true"
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="search"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Buscar variedad"
                      className="w-full rounded-lg border border-gray-300 py-2.5 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100"
                    />
                  </label>
                  <select
                    value={varietyFilter}
                    onChange={(event) => setVarietyFilter(event.target.value)}
                    aria-label="Filtrar variedades"
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100"
                  >
                    <option value="all">Todas</option>
                    <option value="selected">Seleccionadas</option>
                    <option value="unassigned">Sin ningún grupo</option>
                    <option value="other-groups">En otros grupos</option>
                  </select>
                </div>

                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs text-gray-500">
                    {filteredVarieties.length} de {varieties.length} visibles
                  </p>
                  {canUpdateProduct && (
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={selectVisibleVarieties}
                        disabled={isSaving || filteredVarieties.length === 0 || filteredVarieties.every(
                          (variety) => selectedIdSet.has(Number(variety.id))
                        )}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Seleccionar visibles
                      </button>
                      <button
                        type="button"
                        onClick={clearSelectedVarieties}
                        disabled={isSaving || draftVarietyIds.length === 0}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Limpiar selección
                      </button>
                    </div>
                  )}
                </div>

                <div className="mt-3 grid max-h-[360px] gap-2 overflow-y-auto pr-1 sm:grid-cols-2">
                  {filteredVarieties.map((variety) => {
                    const varietyId = Number(variety.id);
                    const otherGroups = (groupNamesByVarietyId.get(varietyId) || []).filter(
                      (group) => group.id !== toId(selectedGroup?.id)
                    );
                    const checked = selectedIdSet.has(varietyId);
                    return (
                      <label
                        key={variety.id}
                        className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-3 transition ${
                          checked ? 'border-blue-300 bg-blue-50/70' : 'border-gray-200 bg-white hover:border-gray-300'
                        } ${!canUpdateProduct || isSaving ? 'cursor-default opacity-75' : ''}`}
                      >
                        <input
                          type="checkbox"
                          name="varietyGroupMembership"
                          value={variety.id}
                          checked={checked}
                          onChange={() => toggleVariety(variety.id)}
                          disabled={!canUpdateProduct || isSaving}
                          className="mt-0.5 h-4 w-4 flex-none accent-blue-600"
                        />
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-semibold text-gray-800">
                            {getVarietyName(variety)}
                          </span>
                          <span className="mt-1 block text-xs text-gray-500">
                            {otherGroups.length
                              ? `También en: ${otherGroups.map((group) => group.name).join(', ')}`
                              : 'Sin otros grupos'}
                          </span>
                        </span>
                      </label>
                    );
                  })}

                  {filteredVarieties.length === 0 && (
                    <p className="col-span-full rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
                      {varieties.length
                        ? 'No hay variedades que coincidan con la búsqueda o el filtro.'
                        : 'Este producto todavía no tiene variedades.'}
                    </p>
                  )}
                </div>
              </fieldset>

            </div>

            <footer className="flex flex-none flex-wrap items-center justify-between gap-3 border-t border-gray-100 bg-white px-4 py-4 sm:px-6">
              <div className="min-h-[24px]">
                {isSaving && (
                  <InlineLoading
                    label={savingAction === 'delete'
                      ? 'Eliminando grupo'
                      : savingAction.startsWith('order-')
                        ? 'Actualizando orden'
                        : 'Guardando grupo'}
                  />
                )}
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                {canUpdateProduct && isDirty && (
                  <button
                    type="button"
                    onClick={discardChanges}
                    disabled={isSaving}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Descartar cambios
                  </button>
                )}
                <button
                  type="button"
                  onClick={requestClose}
                  disabled={isSaving}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cerrar
                </button>
                {canUpdateProduct && (
                  <button
                    type="submit"
                    disabled={isSaving || !draftName.trim() || !isDirty}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {savingAction === 'create'
                      ? 'Creando...'
                      : savingAction === 'save'
                        ? 'Guardando...'
                        : isCreating
                          ? 'Crear grupo'
                          : 'Guardar cambios'}
                  </button>
                )}
              </div>
            </footer>
          </form>
        </div>
      </section>
    </div>
  );

  return createPortal(modal, document.body);
};

export default VarietyGroupConfigurationModal;
